import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Star } from "lucide-react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, TextArea } from "@/components/form/Fields";
import { MultiSelect } from "@/components/form/MultiSelect";
import { cn } from "@/lib/utils";

export const loadTermsTemplates = () => client.get("/voucher-terms").then(({ data }) => data);

// Property's own template first, then the general default
export const pickTerms = (templates, pid) =>
  (pid && templates.find((t) => (t.property_ids || []).includes(pid))) || templates.find((t) => t.default) || null;

function TemplateRow({ t, i, props, onChange, onRemove, onDefault }) {
  const taken = props.filter((p) => p.takenBy !== undefined && p.takenBy !== i).map((p) => p.value);
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3" data-testid={`terms-template-${i}`}>
      <div className="flex items-end gap-2">
        <div className="flex-1"><TextField label="Template name" value={t.name} onChange={(v) => onChange({ name: v })} testid={`terms-name-${i}`} /></div>
        <button type="button" onClick={onDefault} title="Default for new vouchers" data-testid={`terms-default-${i}`}
          className={cn("flex h-10 items-center gap-1 rounded-lg border px-2.5 text-xs font-semibold", t.default ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-400 hover:text-slate-600")}>
          <Star className={cn("h-3.5 w-3.5", t.default && "fill-amber-400")} />{t.default ? "Default" : "Make default"}
        </button>
        <button type="button" onClick={onRemove} className="flex h-10 items-center rounded-lg px-2 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Remove" data-testid={`terms-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <TextArea label="Terms & conditions" value={t.text} onChange={(v) => onChange({ text: v })} rows={5} testid={`terms-text-${i}`} />
      <MultiSelect label="Default for these properties" value={t.property_ids || []} onChange={(v) => onChange({ property_ids: v })}
        options={props.filter((p) => !taken.includes(p.value))} testid={`terms-props-${i}`} placeholder="Optional — choose properties…" />
    </div>
  );
}

export function TermsTemplatesDialog({ open, onClose, properties }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { if (open) loadTermsTemplates().then(setRows).catch(() => toast.error("Could not load templates")); }, [open]);
  const set = (i, p) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const owner = {};
  (rows || []).forEach((r, i) => (r.property_ids || []).forEach((pid) => { owner[pid] = i; }));
  const props = properties.map((p) => ({ value: p.id, label: p.name, takenBy: owner[p.id] }));
  const save = () => client.put("/voucher-terms", { templates: rows }).then(() => { toast.success("T&C templates saved"); onClose(); })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="terms-templates-dialog">
        <DialogHeader><DialogTitle className="font-heading">T&C Templates</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">New vouchers pre-fill with the property's template, or the default. The text can still be changed on each voucher.</p>
        <div className="space-y-3">
          {(rows || []).map((t, i) => (
            <TemplateRow key={t.id || i} t={t} i={i} props={props} onChange={(p) => set(i, p)}
              onRemove={() => setRows((rs) => rs.filter((_, j) => j !== i))}
              onDefault={() => setRows((rs) => rs.map((r, j) => ({ ...r, default: j === i })))} />
          ))}
          <Button variant="outline" size="sm" onClick={() => setRows((rs) => [...(rs || []), { name: "", text: "", default: !(rs || []).length, property_ids: [] }])} data-testid="terms-add"><Plus className="mr-1.5 h-4 w-4" />Add template</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-sky-600 hover:bg-sky-700" onClick={save} disabled={!rows} data-testid="terms-save">Save templates</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
